import React, { useRef, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import Avatar from "../components/ui/Avatar";
import api, { API_BASE_URL } from "../services/api";
import { authService } from "../services/authService";
import { Volume2 } from "lucide-react";
import {
  getSoundPref, setSoundPref, playNewTicketSound, SOUND_OPTIONS,
} from "../utils/notificationSound";

const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, "");
const avatarSrc = (u) => (u?.avatar_url ? `${API_ORIGIN}${u.avatar_url}` : null);

const Perfil = () => {
  const fileRef = useRef(null);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  });

  const company = localStorage.getItem("active_tenant_name") || "—";
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Usuário";

  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name: user.last_name || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [soundPref, setSoundPrefState] = useState(getSoundPref());

  const onSoundChange = (v) => {
    setSoundPref(v);
    setSoundPrefState(v);
    if (v !== "off") playNewTicketSound(v); // toca uma prévia ao escolher
  };

  const [savingName, setSavingName] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const refreshUser = async () => {
    try {
      const fresh = await authService.getMe();
      setUser(fresh);
      setForm({ first_name: fresh.first_name || "", last_name: fresh.last_name || "" });
    } catch {
      /* ignore */
    }
  };

  const flash = (setter, text) => {
    setMsg("");
    setErr("");
    setter(text);
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    try {
      setSavingName(true);
      flash(setMsg, "");
      await api.put("/me/profile", {
        first_name: form.first_name,
        last_name: form.last_name,
      });
      await refreshUser();
      flash(setMsg, "Nome atualizado com sucesso.");
    } catch (e2) {
      flash(setErr, e2.response?.data?.detail || "Erro ao salvar o nome.");
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      flash(setErr, "A nova senha e a confirmação não coincidem.");
      return;
    }
    try {
      setSavingPwd(true);
      flash(setMsg, "");
      await api.post("/me/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      flash(setMsg, "Senha alterada com sucesso.");
    } catch (e2) {
      flash(setErr, e2.response?.data?.detail || "Erro ao alterar a senha.");
    } finally {
      setSavingPwd(false);
    }
  };

  const handleAvatarPick = () => fileRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      flash(setMsg, "");
      const data = new FormData();
      data.append("file", file);
      await api.post("/me/avatar", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshUser();
      flash(setMsg, "Foto atualizada com sucesso.");
    } catch (e2) {
      flash(setErr, e2.response?.data?.detail || "Erro ao enviar a foto.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const src = avatarSrc(user);

  const inputCls =
    "w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "mb-1.5 block text-sm font-medium text-foreground";

  return (
    <Layout>
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Meu perfil</h1>
          <p className="text-muted-foreground">Gerencie seus dados, foto e senha.</p>
        </div>

        {msg && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {msg}
          </div>
        )}
        {err && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Cabeçalho com avatar */}
        <div className="rounded-xl border border-border bg-card shadow-sm p-6">
          <div className="flex items-center gap-5">
            <Avatar
              src={src}
              name={fullName}
              className="h-20 w-20 ring-2 ring-primary/20"
              textClassName="text-2xl"
            />
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-foreground truncate">{fullName}</h2>
              <p className="text-sm text-muted-foreground truncate">
                {user.profile || ""} · {company}
              </p>
              <div className="mt-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAvatarPick}
                  disabled={uploading}
                  className="h-auto px-3 py-1.5"
                >
                  {uploading ? "Enviando..." : src ? "Trocar foto" : "Adicionar foto"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Dados pessoais */}
        <form
          onSubmit={handleSaveName}
          className="rounded-xl border border-border bg-card shadow-sm p-6 space-y-4"
        >
          <h3 className="text-base font-semibold text-foreground">Dados pessoais</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Nome</label>
              <input
                className={inputCls}
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Sobrenome</label>
              <input
                className={inputCls}
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Usuário</label>
              <input className={`${inputCls} opacity-60`} value={user.username || "—"} disabled />
            </div>
            <div>
              <label className={labelCls}>E-mail</label>
              <input className={`${inputCls} opacity-60`} value={user.email || "—"} disabled />
            </div>
            <div>
              <label className={labelCls}>Perfil</label>
              <input className={`${inputCls} opacity-60`} value={user.profile || "—"} disabled />
            </div>
            <div>
              <label className={labelCls}>Empresa</label>
              <input className={`${inputCls} opacity-60`} value={company} disabled />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingName}>
              {savingName ? "Salvando..." : "Salvar dados"}
            </Button>
          </div>
        </form>

        {/* Segurança / senha */}
        <form
          onSubmit={handleChangePassword}
          className="rounded-xl border border-border bg-card shadow-sm p-6 space-y-4"
        >
          <h3 className="text-base font-semibold text-foreground">Segurança</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Senha atual</label>
              <input
                type="password"
                autoComplete="current-password"
                className={inputCls}
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Nova senha</label>
              <input
                type="password"
                autoComplete="new-password"
                className={inputCls}
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Confirmar nova senha</label>
              <input
                type="password"
                autoComplete="new-password"
                className={inputCls}
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                required
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Mínimo 8 caracteres. Você precisa informar a senha atual para confirmar a troca.
          </p>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingPwd}>
              {savingPwd ? "Atualizando..." : "Atualizar senha"}
            </Button>
          </div>
        </form>

        {/* Som de novo chamado (preferência por usuário/dispositivo) */}
        <div className="rounded-xl border border-border bg-card shadow-sm p-6 space-y-4">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Volume2 size={18} className="text-primary" /> Som de novo chamado
          </h3>
          <p className="text-sm text-muted-foreground">
            Toca um aviso quando entra um chamado novo na empresa (com o BenDesk aberto no
            computador). A escolha vale só para você, neste dispositivo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className={labelCls}>Tipo de som</label>
              <select
                className={inputCls}
                value={soundPref}
                onChange={(e) => onSoundChange(e.target.value)}
              >
                {SOUND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Button
                type="button"
                variant="outline"
                disabled={soundPref === "off"}
                onClick={() => playNewTicketSound(soundPref)}
                className="h-auto px-4 py-2 text-gray-700"
              >
                Testar som
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Salvo automaticamente. No iPhone/Safari o som pode exigir um toque na tela antes
            de tocar sozinho.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Perfil;
